import { useEffect, useMemo, useState } from "react";
import { ActionButton } from "#/components/shared/buttons/ActionButtons";
import Card from "#/components/shared/card";
import Input from "#/components/shared/input";
import Typography from "#/components/shared/typography";
import { ThemedSwitch } from "#/components/shared/themed-switch";
import { ThemedButtonGroup } from "#/components/shared/forms/ThemedButtonGroup";
import ThemedSelect from "#/components/shared/themed-select";
import useOutreachSettingsList from "#/admin/outreach-settings/hooks/useOutreachSettingsList";
import useOutreachSettingsMutations from "#/admin/outreach-settings/hooks/useOutreachSettingsMutations";
import { OutreachSettingsMode } from "#/services/outreachSettings";

type FormState = {
  enabled: boolean;
  mode: OutreachSettingsMode;
  targetHour: string;
  targetDayOfWeek: string;
  minIntervalDays: string;
};

type FormErrors = Partial<Record<keyof FormState, string>>;

const defaultFormState: FormState = {
  enabled: true,
  mode: "daily",
  targetHour: "9",
  targetDayOfWeek: "1",
  minIntervalDays: "7",
};

const modeOptions = [
  {
    value: "daily",
    label: "Daily",
    description: "Send outreach every day.",
  },
  {
    value: "weekly",
    label: "Weekly",
    description: "Send outreach on a specific day of the week.",
  },
];

const dayOptions = [
  { value: "0", label: "Sunday" },
  { value: "1", label: "Monday" },
  { value: "2", label: "Tuesday" },
  { value: "3", label: "Wednesday" },
  { value: "4", label: "Thursday" },
  { value: "5", label: "Friday" },
  { value: "6", label: "Saturday" },
];

function formatSettingsLabel(settings: {
  id: string;
  mode: OutreachSettingsMode;
  target_hour: number;
  target_day_of_week: number | null;
  enabled: boolean;
}) {
  const timeLabel = `${settings.target_hour.toString().padStart(2, "0")}:00`;
  const dayLabel =
    settings.mode === "weekly" && settings.target_day_of_week !== null
      ? dayOptions.find((option) => option.value === String(settings.target_day_of_week))
          ?.label ?? "Weekly"
      : "Daily";
  return `${dayLabel} @ ${timeLabel} • ${settings.enabled ? "Enabled" : "Disabled"}`;
}

export function OutreachSettingsPanel() {
  const listQuery = useOutreachSettingsList();
  const { createSettings, updateSettings } = useOutreachSettingsMutations();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [errors, setErrors] = useState<FormErrors>({});

  const settingsList = listQuery.data ?? [];

  useEffect(() => {
    if (!selectedId && settingsList.length > 0) {
      setSelectedId(settingsList[0].id);
    }
  }, [selectedId, settingsList]);

  useEffect(() => {
    if (!selectedId) {
      setFormState(defaultFormState);
      return;
    }

    const active = settingsList.find((item) => item.id === selectedId);
    if (!active) return;

    setFormState({
      enabled: Boolean(active.enabled),
      mode: active.mode,
      targetHour: String(active.target_hour ?? 9),
      targetDayOfWeek:
        active.target_day_of_week !== null && active.target_day_of_week !== undefined
          ? String(active.target_day_of_week)
          : "1",
      minIntervalDays: String(active.min_interval_days ?? 7),
    });
  }, [selectedId, settingsList]);

  const selectOptions = useMemo(
    () => [
      { value: "", label: "Create new settings" },
      ...settingsList.map((item) => ({
        value: item.id,
        label: formatSettingsLabel(item),
      })),
    ],
    [settingsList]
  );

  const handleChange = (field: keyof FormState, value: string | boolean) => {
    setFormState((current) => ({
      ...current,
      [field]: value,
    }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const handleSubmit = async () => {
    const nextErrors: FormErrors = {};
    const targetHour = Number(formState.targetHour);
    const minIntervalDays = Number(formState.minIntervalDays);
    const targetDay = Number(formState.targetDayOfWeek);

    if (!Number.isFinite(targetHour) || targetHour < 0 || targetHour > 23) {
      nextErrors.targetHour = "Target hour must be between 0 and 23.";
    }

    if (
      !Number.isFinite(minIntervalDays) ||
      minIntervalDays < 1 ||
      minIntervalDays > 365
    ) {
      nextErrors.minIntervalDays = "Min interval must be between 1 and 365.";
    }

    if (formState.mode === "weekly") {
      if (!Number.isFinite(targetDay) || targetDay < 0 || targetDay > 6) {
        nextErrors.targetDayOfWeek = "Pick a day of the week (0–6).";
      }
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const payload = {
      enabled: formState.enabled,
      mode: formState.mode,
      target_hour: targetHour,
      target_day_of_week: formState.mode === "weekly" ? targetDay : null,
      min_interval_days: minIntervalDays,
    };

    if (selectedId) {
      await updateSettings.mutateAsync({ id: selectedId, payload });
    } else {
      const created = await createSettings.mutateAsync(payload);
      if (created?.id) {
        setSelectedId(created.id);
      }
    }
  };

  const isSaving = createSettings.isPending || updateSettings.isPending;

  return (
    <Card className="w-full" variant="cream" elevation={1}>
      <Card.Header variant="cream" divider className="bg-cream-100">
        <div className="flex flex-col gap-1">
          <Typography variant="h3" className="text-brown-900">
            Outreach settings
          </Typography>
          <Typography variant="body2" className="text-brown-600">
            Control when automated outreach is sent.
          </Typography>
        </div>
      </Card.Header>
      <Card.Body className="space-y-6 bg-white">
        {listQuery.isError ? (
          <div className="rounded-lg border border-terracotta/30 bg-cream-50 p-4">
            <Typography variant="body2" className="text-terracotta">
              {listQuery.error instanceof Error
                ? listQuery.error.message
                : "Unable to load outreach settings."}
            </Typography>
            <div className="mt-3">
              <ActionButton size="sm" onClick={() => listQuery.refetch()}>
                Retry
              </ActionButton>
            </div>
          </div>
        ) : null}

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <ThemedSelect
              key={selectedId ?? "new-settings"}
              id="outreach-settings-select"
              label="Settings"
              options={selectOptions}
              initialValue={selectedId ?? ""}
              onChange={(event) =>
                setSelectedId(event.target.value || null)
              }
              placeholder="Create new settings"
            />
            {settingsList.length > 0 ? (
              <ActionButton
                size="sm"
                tone="secondary"
                onClick={() => setSelectedId(null)}
              >
                New settings
              </ActionButton>
            ) : null}
          </div>

          <ThemedSwitch
            name="outreach-settings-enabled"
            label="Enabled"
            description="Toggle outreach automation on or off."
            value={formState.enabled}
            onChange={(nextValue) => handleChange("enabled", nextValue)}
            labelOnValue="On"
            labelOffValue="Off"
            size="sm"
            showSeparator={false}
          />
        </div>

        <ThemedButtonGroup
          label="Mode"
          options={modeOptions}
          value={formState.mode}
          onChange={(value) => handleChange("mode", value as OutreachSettingsMode)}
          fullWidth
        />

        <div className="grid gap-4 md:grid-cols-3">
          <Input
            id="outreach-target-hour"
            label="Target hour"
            type="number"
            min={0}
            max={23}
            value={formState.targetHour}
            onChange={(event) => handleChange("targetHour", event.target.value)}
            error={errors.targetHour}
            helperText="0–23"
            fullWidth
          />
          <Input
            id="outreach-target-day"
            label="Target day of week"
            type="number"
            min={0}
            max={6}
            value={formState.targetDayOfWeek}
            onChange={(event) =>
              handleChange("targetDayOfWeek", event.target.value)
            }
            error={errors.targetDayOfWeek}
            helperText="0=Sun … 6=Sat"
            disabled={formState.mode !== "weekly"}
            fullWidth
          />
          <Input
            id="outreach-min-interval"
            label="Min interval (days)"
            type="number"
            min={1}
            max={365}
            value={formState.minIntervalDays}
            onChange={(event) =>
              handleChange("minIntervalDays", event.target.value)
            }
            error={errors.minIntervalDays}
            helperText="1–365"
            fullWidth
          />
        </div>
      </Card.Body>
      <Card.Footer className="bg-white">
        <div className="flex flex-wrap items-center gap-3">
          <ActionButton
            onClick={handleSubmit}
            isLoading={isSaving}
            disabled={listQuery.isLoading || isSaving}
          >
            {selectedId ? "Save settings" : "Create settings"}
          </ActionButton>
          {listQuery.isLoading ? (
            <Typography variant="body2" className="text-brown-600">
              Loading settings…
            </Typography>
          ) : null}
        </div>
      </Card.Footer>
    </Card>
  );
}

export default OutreachSettingsPanel;
